import { forwardRef, memo, useEffect, useRef } from 'react';
import { Group, Rect, Text, Line } from 'react-konva';
import Konva from 'konva';
import { Node } from '../../../../core/models/scene';

type OrgVariant =
  | 'role'
  | 'department'
  | 'team'
  | 'vacant';

interface OrgChartShapeNodeProps {
  node: Node;
  selected: boolean;
  onDragStart?: (pos: { x: number; y: number }) => void;
  onDragMove?: (pos: { x: number; y: number }) => void;
  onDragEnd?: (pos: { x: number; y: number }) => void;
  onClick?: (e: any) => void;
  onTransformStart?: (e: any) => void;
  onTransformEnd?: (e: any) => void;
  onTransform?: (e: any) => void;
  canWrite?: boolean;
}

const resolveVariant = (node: Node): OrgVariant => {
  const attrs = (node.attrs as Record<string, unknown>) ?? {};
  const candidates = [attrs.variant, attrs.shapeType, node.type];
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const normalized = candidate.toLowerCase() as OrgVariant;
      if (
        normalized === 'role' ||
        normalized === 'department' ||
        normalized === 'team' ||
        normalized === 'vacant'
      ) {
        return normalized;
      }
    }
  }
  return 'role';
};

const OrgChartShapeNodeBase = forwardRef<Konva.Group, OrgChartShapeNodeProps>(({ 
  node,
  onDragStart,
  onDragMove,
  onDragEnd,
  onClick,
  onTransformStart,
  onTransformEnd,
  onTransform,
  canWrite = true,
}, ref) => {
  const width = (node.width ?? node.w ?? 200) as number;
  const height = (node.height ?? node.h ?? 120) as number;
  const variant = resolveVariant(node);
  const attrs = (node.attrs as Record<string, unknown>) ?? {};

  const groupRef = useRef<Konva.Group | null>(null);
  const bodyRectRef = useRef<Konva.Rect | null>(null);
  const headerRectRef = useRef<Konva.Rect | null>(null);
  const separatorRef = useRef<Konva.Line | null>(null);
  const titleRef = useRef<Konva.Text | null>(null);
  const subtitleRef = useRef<Konva.Text | null>(null);

  const fillColor =
    (node.fillColor as string) ||
    (attrs.fillColor as string) ||
    '#FFFFFF';
  const headerColor =
    (attrs.headerColor as string) ||
    '#0EA5E9';
  const strokeColor =
    (node.strokeColor as string) ||
    (attrs.strokeColor as string) ||
    '#1E293B';
  const strokeWidth =
    typeof node.strokeWidth === 'number'
      ? node.strokeWidth
      : typeof attrs.strokeWidth === 'number'
        ? (attrs.strokeWidth as number)
        : 2;

  const title =
    (attrs.title as string) ||
    (typeof node.text === 'string' ? node.text : '') ||
    (node.name as string) ||
    '';
  const subtitle = (attrs.subtitle as string) || '';
  const textColor = (attrs.textColor as string) || '#0F172A';
  const subtitleColor = (attrs.subtitleColor as string) || '#475569';

  const isVacant = variant === 'vacant';
  const getHeaderHeight = (h: number) =>
    variant === 'vacant' ? 0 : Math.min(36, h * 0.32);
  const headerHeight = getHeaderHeight(height);

  const fireDrag = (handler?: (pos: { x: number; y: number }) => void) => (e: any) => {
    if (!handler) return;
    const pos = e.target.position();
    handler({ x: pos.x, y: pos.y });
  };

  const applyDimensions = (nextWidth: number, nextHeight: number) => {
    const group = groupRef.current;
    if (group) {
      group.width(nextWidth);
      group.height(nextHeight);
    }
    if (bodyRectRef.current) {
      bodyRectRef.current.width(nextWidth);
      bodyRectRef.current.height(nextHeight);
    }
    const nextHeaderHeight = getHeaderHeight(nextHeight);
    if (headerRectRef.current) {
      headerRectRef.current.width(nextWidth);
      headerRectRef.current.height(nextHeaderHeight);
    }
    if (separatorRef.current) {
      separatorRef.current.points([0, nextHeaderHeight, nextWidth, nextHeaderHeight]);
    }
    if (titleRef.current) {
      const titleY = (variant === 'role' || variant === 'department' || variant === 'team') ? 8 : 16;
      titleRef.current.width(Math.max(0, nextWidth - 32));
      titleRef.current.height(Math.max(0, nextHeaderHeight - 16));
      titleRef.current.x(16);
      titleRef.current.y(titleY);
    }
    if (subtitleRef.current) {
      subtitleRef.current.width(Math.max(0, nextWidth - 32));
      subtitleRef.current.height(Math.max(0, nextHeight - nextHeaderHeight - 16));
      subtitleRef.current.x(16);
      subtitleRef.current.y(nextHeaderHeight + 8);
    }
  };

  useEffect(() => {
    applyDimensions(width, height);
  }, [width, height, variant]);

  const handleTransformEnd = (e: any) => {
    // Defer to canvas-level handler to read the scale and persist size; avoid resetting here.
    onTransformEnd?.(e);
  };

  return (
    <Group
      ref={(nodeEl) => {
        groupRef.current = nodeEl as Konva.Group | null;
        if (typeof ref === 'function') ref(nodeEl as any);
        else if (ref && typeof ref === 'object') (ref as any).current = nodeEl;
      }}
      id={node.id}
      name="shape"
      x={node.x}
      y={node.y}
      width={width}
      height={height}
      draggable={canWrite}
      onDragStart={fireDrag(onDragStart)}
      onDragMove={fireDrag(onDragMove)}
      onDragEnd={fireDrag(onDragEnd)}
      onClick={onClick}
      onTransformStart={onTransformStart}
      onTransformEnd={handleTransformEnd}
      onTransform={onTransform}
    >
      <Rect
        ref={bodyRectRef as any}
        x={0}
        y={0}
        width={width}
        height={height}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeEnabled={strokeWidth > 0}
        dashEnabled={isVacant}
        dash={isVacant ? [8, 6] : undefined}
        perfectDrawEnabled={false}
        cornerRadius={8}
      />

      {(variant === 'role' || variant === 'department' || variant === 'team') && (
        <Rect
          ref={headerRectRef as any}
          x={0}
          y={0}
          width={width}
          height={headerHeight}
          fill={headerColor}
          cornerRadius={[8, 8, 0, 0]}
          listening={false}
          perfectDrawEnabled={false}
        />
      )}

      {variant === 'department' && (
        <Line
          ref={separatorRef as any}
          points={[0, headerHeight, width, headerHeight]}
          stroke={strokeColor}
          strokeWidth={1}
          opacity={0.35}
          dash={[4, 4]}
          listening={false}
        />
      )}

      <Text
        ref={titleRef as any}
        x={16}
        y={variant === 'role' || variant === 'department' || variant === 'team' ? 8 : 16}
        width={Math.max(0, width - 32)}
        height={headerHeight - 16}
        text={title}
        align="left"
        verticalAlign="middle"
        ellipsis
        listening={false}
        fontSize={18}
        fontStyle="600"
        fill={variant === 'role' || variant === 'department' || variant === 'team' ? '#FFFFFF' : textColor}
      />

      {subtitle && (
        <Text
          ref={subtitleRef as any}
          x={16}
          y={headerHeight + 8}
          width={Math.max(0, width - 32)}
          height={Math.max(0, height - headerHeight - 16)}
          text={subtitle}
          align="left"
          verticalAlign="top"
          listening={false}
          fontSize={14}
          fill={subtitleColor}
        />
      )}

      {isVacant && (
        <Text
          x={16}
          y={height / 2 - 12}
          width={Math.max(0, width - 32)}
          align="center"
          verticalAlign="middle"
          listening={false}
          fontSize={16}
          fontStyle="italic"
          fill={subtitleColor}
          text={title || 'Vacant'}
        />
      )}
    </Group>
  );
});
OrgChartShapeNodeBase.displayName = 'OrgChartShapeNode';

export const OrgChartShapeNode = memo(OrgChartShapeNodeBase);

export default OrgChartShapeNode;
